import React, { useState, useEffect } from 'react';
import { sendMessageNow, scheduleMessage, getAllScheduledMessages, cancelScheduledMessage, deleteScheduledMessage } from '../services/scheduledMessages';
import { getAllTemplates } from '../services/messageTemplates';

const EmailBroadcast = () => {
  const [activeTab, setActiveTab] = useState('send');
  const [templates, setTemplates] = useState([]);
  const [scheduledMessages, setScheduledMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    subject: '',
    body: '',
    recipient_filter: 'all',
    scheduled_at: '',
    template_id: ''
  });

  useEffect(() => {
    fetchTemplates();
    if (activeTab === 'scheduled') {
      fetchScheduledMessages();
    }
  }, [activeTab]);

  const fetchTemplates = async () => {
    try {
      const response = await getAllTemplates();
      const templateList = response.templates || response.data || [];
      setTemplates(templateList.filter(t => t.is_active));
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    }
  };

  const fetchScheduledMessages = async () => {
    try {
      const response = await getAllScheduledMessages();
      setScheduledMessages(response.messages || response.data || []);
    } catch (error) {
      console.error('Failed to fetch scheduled messages:', error);
    }
  };

  const handleTemplateSelect = (templateId) => {
    const template = templates.find(t => t.id === parseInt(templateId));
    if (template) {
      setFormData({
        ...formData,
        template_id: templateId,
        subject: template.subject,
        body: template.body
      });
    } else {
      setFormData({
        ...formData,
        template_id: '',
        subject: '',
        body: ''
      });
    }
  };

  const handleSendNow = async (e) => {
    e.preventDefault();
    if (!window.confirm(`Send email to ${formData.recipient_filter} users now?`)) return;

    try {
      setLoading(true);
      const data = {
        subject: formData.subject,
        body: formData.body,
        recipient_filter: formData.recipient_filter
      };
      await sendMessageNow(data);
      alert('Email sent successfully!');
      setFormData({ subject: '', body: '', recipient_filter: 'all', scheduled_at: '', template_id: '' });
    } catch (error) {
      alert('Failed to send email: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSchedule = async (e) => {
    e.preventDefault();
    if (!formData.scheduled_at) {
      alert('Please select a date and time');
      return;
    }

    try {
      setLoading(true);
      const data = {
        subject: formData.subject,
        body: formData.body,
        recipient_filter: formData.recipient_filter,
        scheduled_at: new Date(formData.scheduled_at).toISOString()
      };
      await scheduleMessage(data);
      alert('Email scheduled successfully!');
      setFormData({ subject: '', body: '', recipient_filter: 'all', scheduled_at: '', template_id: '' });
      setActiveTab('scheduled');
    } catch (error) {
      alert('Failed to schedule email: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async (id) => {
    if (!window.confirm('Cancel this scheduled message?')) return;
    try {
      await cancelScheduledMessage(id);
      fetchScheduledMessages();
    } catch (error) {
      alert('Failed to cancel message: ' + error.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this scheduled message?')) return;
    try {
      await deleteScheduledMessage(id);
      fetchScheduledMessages();
    } catch (error) {
      alert('Failed to delete message: ' + error.message);
    }
  };

  return (
    <div className="p-4 md:p-6">
      <h1 className="text-2xl md:text-3xl font-bold mb-6">Email Broadcast & Scheduling</h1>

      <div className="mb-6 flex gap-2 md:gap-4 border-b overflow-x-auto">
        <button
          onClick={() => setActiveTab('send')}
          className={`px-3 md:px-4 py-2 text-sm md:text-base whitespace-nowrap ${activeTab === 'send' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-600'}`}
        >
          Send Now
        </button>
        <button
          onClick={() => setActiveTab('schedule')}
          className={`px-3 md:px-4 py-2 text-sm md:text-base whitespace-nowrap ${activeTab === 'schedule' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-600'}`}
        >
          Schedule
        </button>
        <button
          onClick={() => setActiveTab('scheduled')}
          className={`px-3 md:px-4 py-2 text-sm md:text-base whitespace-nowrap ${activeTab === 'scheduled' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-600'}`}
        >
          Scheduled Messages
        </button>
      </div>

      {(activeTab === 'send' || activeTab === 'schedule') && (
        <div className="bg-white rounded-lg shadow-sm border p-4 md:p-6">
          <form onSubmit={activeTab === 'send' ? handleSendNow : handleSchedule}>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">Use Template (Optional)</label>
              <select
                value={formData.template_id}
                onChange={(e) => handleTemplateSelect(e.target.value)}
                className="w-full border rounded px-3 py-2"
              >
                <option value="">-- Select Template --</option>
                {templates.map(template => (
                  <option key={template.id} value={template.id}>
                    {template.name} ({template.template_type})
                  </option>
                ))}
              </select>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">Recipient Filter</label>
              <select
                value={formData.recipient_filter}
                onChange={(e) => setFormData({ ...formData, recipient_filter: e.target.value })}
                className="w-full border rounded px-3 py-2"
                required
              >
                <option value="all">All Users</option>
                <option value="verified">Verified Users Only</option>
                <option value="unverified">Unverified Users Only</option>
                <option value="subscribed">Active Subscribers</option>
                <option value="unsubscribed">Non-Subscribers</option>
              </select>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">Subject</label>
              <input
                type="text"
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                className="w-full border rounded px-3 py-2"
                placeholder="Email subject - use {{firstName}}, {{email}}, etc."
                required
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">Message Body (HTML)</label>
              <textarea
                value={formData.body}
                onChange={(e) => setFormData({ ...formData, body: e.target.value })}
                className="w-full border rounded px-3 py-2 h-48"
                placeholder="<h2>Hi {{firstName}},</h2><p>Your message here...</p>"
                required
              />
            </div>

            {activeTab === 'schedule' && (
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">Schedule Date & Time</label>
                <input
                  type="datetime-local"
                  value={formData.scheduled_at}
                  onChange={(e) => setFormData({ ...formData, scheduled_at: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                  required
                />
              </div>
            )}

            <div className="mb-4">
              <p className="text-sm text-gray-600">
                <strong>Available variables:</strong> {'{{'}firstName{'}}'}, {'{{'}lastName{'}}'}, {'{{'}email{'}}'}, {'{{'}daysSinceRegistration{'}}'}, {'{{'}verificationLink{'}}'}, {'{{'}subscriptionLink{'}}'}
              </p>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
            >
              {loading ? 'Processing...' : (activeTab === 'send' ? 'Send Now' : 'Schedule Email')}
            </button>
          </form>
        </div>
      )}

      {activeTab === 'scheduled' && (
        <div className="grid gap-4">
          {scheduledMessages.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <p className="text-gray-500">No scheduled messages found.</p>
            </div>
          ) : (
            scheduledMessages.map((message) => (
              <div key={message.id} className="bg-white border rounded-lg p-4 shadow-sm">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold">{message.subject}</h3>
                    <p className="text-gray-600 mt-1">
                      <strong>To:</strong> {message.recipient_filter}
                    </p>
                    <p className="text-gray-600">
                      <strong>Scheduled for:</strong> {new Date(message.scheduled_at).toLocaleString()}
                    </p>
                    <div className="mt-2">
                      <span className={`inline-block px-3 py-1 rounded-full text-sm ${
                        message.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                        message.status === 'sent' ? 'bg-green-100 text-green-800' :
                        message.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {message.status}
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {message.status === 'pending' && (
                      <button
                        onClick={() => handleCancel(message.id)}
                        className="px-4 py-2 bg-yellow-100 text-yellow-800 rounded hover:bg-yellow-200"
                      >
                        Cancel
                      </button>
                    )}
                    <button
                      onClick={() => handleDelete(message.id)}
                      className="px-4 py-2 bg-red-100 text-red-800 rounded hover:bg-red-200"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default EmailBroadcast;
